import { ref, nextTick, provide, inject, getCurrentInstance } from 'vue';
import type { Ref, InjectionKey } from 'vue';
import useVuelidate from '@vuelidate/core';
import type { GlobalConfig, ValidationArgs } from '@vuelidate/core';
import { useToasted } from './useToasted';
import { useFormRequestHandler } from './useFormRequestHandler';
import { validateRules } from '~/utils/validation';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { useCallout } from './useCallout';
import { type RouteLocationAsPathGeneric, type RouteLocationAsRelativeGeneric, useRouter } from 'vue-router';

type BeforeSubmitCallback = () => void | Promise<void>;

export const FormSubmissionKey: InjectionKey<{
    onBeforeSubmit: (callback: BeforeSubmitCallback) => void;
    offBeforeSubmit: (callback: BeforeSubmitCallback) => void;
}> = Symbol('FormSubmission');

interface FormSubmissionConfig<TForm extends Record<string, unknown>> {
    data: TForm | Ref<TForm>;
    validation?: ValidationArgs | Ref<ValidationArgs>;
    scope?: GlobalConfig['$scope'];
    submit: () => Promise<unknown>;
    reset?: () => void;
    notification?: () => {
        message: string;
        redirect?: string;
        vueRedirect?: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric;
        action?: {
            label: string;
            icon?: IconDefinition;
        } & (
            | { href: string; onClick?: never; to?: never }
            | { onClick: (event: MouseEvent) => void; href?: never; to?: never }
            | { onClick: (event: MouseEvent) => void; href: string; to?: never }
            | {
                  to: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric;
                  onClick?: never;
                  href?: never;
              }
        );
    };
    onSuccess?: () => void;
    preserveScroll: (() => boolean) | boolean;
}

export function useFormSubmission<TForm extends Record<string, unknown>>(config: FormSubmissionConfig<TForm>) {
    const toasted = useToasted();
    const callout = useCallout();
    const router = useRouter();

    const { $externalResults, catchError } = useFormRequestHandler();

    const processing = ref(false);
    const wasSuccessful = ref(false);
    let submitting = false;

    const validation = useVuelidate(config.validation || {}, config.data, { $externalResults, $scope: config.scope });

    const beforeSubmitCallbacks: BeforeSubmitCallback[] = [];

    if (getCurrentInstance()) {
        provide(FormSubmissionKey, {
            onBeforeSubmit: (callback: BeforeSubmitCallback) => {
                beforeSubmitCallbacks.push(callback);
            },
            offBeforeSubmit: (callback: BeforeSubmitCallback) => {
                const index = beforeSubmitCallbacks.indexOf(callback);
                if (index > -1) {
                    beforeSubmitCallbacks.splice(index, 1);
                }
            },
        });
    }

    const reset = () => {
        validation.value.$reset();
        config.reset?.();
    };

    const submit = async () => {
        if (submitting) {
            return;
        }

        try {
            submitting = true;
            wasSuccessful.value = false;

            if (beforeSubmitCallbacks.length > 0) {
                await Promise.all(beforeSubmitCallbacks.map((callback) => callback()));
            }

            const isValid = await validateRules(validation);
            if (!isValid) {
                return;
            }

            processing.value = true;

            const response = await config.submit();

            wasSuccessful.value = true;

            // built before the reset so the message can describe what was submitted
            const notification =
                typeof config.notification === 'function' ? config.notification() : config.notification;

            reset();
            config.onSuccess?.();

            await nextTick();

            const preserveScroll =
                typeof config.preserveScroll === 'function' ? config.preserveScroll() : config.preserveScroll ?? true;

            if (!preserveScroll && !notification?.redirect) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }

            if (notification) {
                if (notification.redirect || notification.vueRedirect) {
                    // The callout takes only href or onClick.
                    const action = notification.action;
                    const calloutAction =
                        action && 'to' in action && action.to !== undefined
                            ? { label: action.label, icon: action.icon, onClick: () => void router.push(action.to) }
                            : action;

                    const calloutTtlSeconds = 15;
                    const calloutId = callout.show('form-submission', notification.message, {
                        type: 'success',
                        ttl: calloutTtlSeconds,
                        action: calloutAction,
                    });

                    if (notification.redirect) {
                        window.location.assign(notification.redirect);
                    } else if (notification.vueRedirect) {
                        await router.push(notification.vueRedirect);
                        setTimeout(() => callout.remove(calloutId), calloutTtlSeconds * 1000);
                    }
                } else if (notification.action && 'to' in notification.action && notification.action.to) {
                    const navigateTo = notification.action.to;
                    const toastId = toasted.show({
                        type: 'success',
                        title: notification.message,
                        action: {
                            label: notification.action.label,
                            icon: notification.action.icon,
                            onClick: () => {
                                toasted.dismiss(toastId);
                                router.push(navigateTo);
                            },
                        },
                        closeable: false,
                    });
                } else {
                    toasted.show({
                        type: 'success',
                        title: notification.message,
                        action: notification.action,
                        closeable: !notification.action,
                    });
                }
            }

            return response;
        } catch (error: unknown) {
            wasSuccessful.value = false;

            await catchError(error);
        } finally {
            processing.value = false;
            submitting = false;
        }
    };

    return {
        submit,
        processing,
        wasSuccessful,
        validation,
        $externalResults,
    };
}

export function useFormSubmissionChild() {
    const noop = () => {};

    if (!getCurrentInstance()) {
        return {
            onBeforeSubmit: noop,
            offBeforeSubmit: noop,
        };
    }

    const formSubmission = inject(FormSubmissionKey, null);

    return {
        onBeforeSubmit: formSubmission?.onBeforeSubmit ?? noop,
        offBeforeSubmit: formSubmission?.offBeforeSubmit ?? noop,
    };
}
